import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
} from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import type { Project } from '../types';

export function useProjects(params?: { status?: string }) {
  return useQuery({
    queryKey: queryKeys.projects(params),
    queryFn: () => listProjects(params),
  });
}

/**
 * Same data and cache entry as useProjects(), minus the Parking Lot
 * placeholder project. Reach for this whenever you're counting, listing, or
 * aggregating over projects — the Parking Lot is a holding area, not a
 * project, and must never be included. Use useProjects() only when you
 * specifically need the Parking Lot itself (e.g. rendering its own card or
 * section).
 */
export function useRegularProjects(params?: { status?: string }) {
  return useQuery({
    queryKey: queryKeys.projects(params),
    queryFn: () => listProjects(params),
    select: (projects) => projects.filter((p) => !p.isParkingLot),
  });
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.project(projectId ?? ''),
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Project> }) =>
      updateProject(id, data),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: queryKeys.project(id) });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
